
import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { WeeklyHours, DayHours, DAYS_OF_WEEK } from '@communitly/shared';
import { TimeInput } from '@/components/ui/time-input';

interface SidebarOpeningHoursFormProps {
  openingHours: WeeklyHours;
  onUpdate: (hours: WeeklyHours) => void;
  timeFormat?: '12' | '24';
}

const DAY_NAMES = {
  monday: 'Monday',
  tuesday: 'Tuesday', 
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday'
} as const;

const SidebarOpeningHoursForm: React.FC<SidebarOpeningHoursFormProps> = ({ 
  openingHours, 
  onUpdate,
  timeFormat = '12'
}) => {
  const updateDayHours = (day: keyof WeeklyHours, dayHours: DayHours) => {
    const updatedHours = {
      ...openingHours,
      [day]: dayHours
    };
    onUpdate(updatedHours);
  };

  return (
    <div className="space-y-4">
      {DAYS_OF_WEEK.map(day => {
        const dayHours = openingHours[day] || { open: '09:00', close: '17:00', closed: false };
        
        return (
          <div key={day} className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-2 h-2 rounded-full ${dayHours.closed ? 'bg-red-400' : 'bg-green-400'}`} />
                <Label className="text-sm font-medium min-w-[90px]">
                  {DAY_NAMES[day]}
                </Label>
              </div>
              <Switch
                checked={!dayHours.closed}
                onCheckedChange={(checked) => 
                  updateDayHours(day, { ...dayHours, closed: !checked })
                }
              />
            </div>
            
            {!dayHours.closed && (
              <div className="ml-5 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Opens</Label>
                    <TimeInput
                      value={dayHours.open}
                      onChange={(time) => updateDayHours(day, { ...dayHours, open: time })}
                      timeFormat={timeFormat}
                      className="h-8 text-xs"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Closes</Label>
                    <TimeInput
                      value={dayHours.close}
                      onChange={(time) => updateDayHours(day, { ...dayHours, close: time })}
                      timeFormat={timeFormat}
                      className="h-8 text-xs"
                    />
                  </div>
                </div>
              </div>
            )}
            
            {dayHours.closed && (
              <div className="ml-5 text-xs text-muted-foreground">
                Closed
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SidebarOpeningHoursForm;
